import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { useClosestAncestor } from '../../../hooks/useClosestAncestor';
import {
  selectCurrentCommand,
  selectSearchFocusIdForCatalog,
} from '../../../store/selectors/tree/tree';
import { CATEGORIZATION, NODE_COPY, NODE_CUT } from '../../../utils/command';
import type { NodeBase, NodeItem } from '../../../entities/Node';
import type { RootState } from '../../../store';

const directiveClassNames: Record<string, string> = {
  [CATEGORIZATION]: 'rc-tree-treenode-cut',
  [NODE_COPY]: 'rc-tree-treenode-copy',
  [NODE_CUT]: 'rc-tree-treenode-cut',
};

interface ActionsDirectiveProps {
  node: NodeBase | NodeItem;
  children: ReactNode;
}

const ActionsDirective = ({ node, children }: ActionsDirectiveProps) => {
  const isSearchFocus =
    useSelector((state: RootState) =>
      selectSearchFocusIdForCatalog(state, node.catalogId),
    ) === node.id;

  const { directive, nodeItems } = useSelector(selectCurrentCommand);
  const [elementRef, ancestorRef] = useClosestAncestor('.rc-tree-treenode');

  useEffect(() => {
    if (!ancestorRef.current) {
      return;
    }

    ancestorRef.current.classList.remove(
      directiveClassNames[NODE_COPY],
      directiveClassNames[NODE_CUT],
    );
    if (directive && nodeItems.some((ni) => ni.node.id === node.id)) {
      const className = directiveClassNames[directive];
      if (className) {
        ancestorRef.current.classList.add(className);
      }
    }
  }, [ancestorRef, directive, node, nodeItems]);

  useLayoutEffect(() => {
    if (isSearchFocus && ancestorRef.current) {
      ancestorRef.current.scrollIntoView();
    }
  }, [isSearchFocus, ancestorRef]);

  return <div ref={elementRef}>{children}</div>;
};

export default ActionsDirective;
